/* eslint-disable */
import PropTypes from 'prop-types';
import { I18N_DIRECTION_LEFT_TO_RIGHT, I18N_DIRECTION_RIGHT_TO_LEFT } from '../../../Constants/I18n/I18n.constants';
import { THEME_PALETTE_TYPE_DARK, THEME_PALETTE_TYPE_LIGHT } from '../../../Constants/Theme/Theme.constants';

/**
 * A material-ui classes Object
 */
const classes = PropTypes.object;

/**
 * A material-ui compliant complex color
 */
const colorComplex = PropTypes.shape({
  50: PropTypes.string.isRequired,
  100: PropTypes.string.isRequired,
  200: PropTypes.string.isRequired,
  300: PropTypes.string.isRequired,
  400: PropTypes.string.isRequired,
  500: PropTypes.string.isRequired,
  600: PropTypes.string.isRequired,
  700: PropTypes.string.isRequired,
  800: PropTypes.string.isRequired,
  900: PropTypes.string.isRequired,
  A100: PropTypes.string.isRequired,
  A200: PropTypes.string.isRequired,
  A400: PropTypes.string.isRequired,
  A700: PropTypes.string.isRequired,
});

/**
 * A material-ui compliant simple color
 */
const colorSimple = PropTypes.shape({
  /**
   * The contrast text color to use. Found by `getContrastText(color['500'])` for primary or error colors and `getContrastText(color['A400'])` for secondary colors
   * Ommiting this value, ie, not marking it as required forces material-ui to auto generate the correct value dynamically based off of `main`.
   * This is the recommended behaviour. Only provide this value if you know what you are doing.
   */
  contrastText: PropTypes.string,

  /**
   * A dark version of the `main` color. Either `color[700]` for primary or error colors and `color['A700']` for secondary colors.
   * Ommiting this value, ie, not marking it as required forces material-ui to auto generate the correct value dynamically based off of `main`.
   * This is the recommended behaviour. Only provide this value if you know what you are doing.
   */
  dark: PropTypes.string,

  /**
   * A light version of the `main` color. Either `color[300]` for primary or error colors and `color['A200']` for secondary colors
   * Ommiting this value, ie, not marking it as required forces material-ui to auto generate the correct value dynamically based off of `main`.
   * This is the recommended behaviour. Only provide this value if you know what you are doing.
   */
  light: PropTypes.string,

  /**
   * The `main` color. Either `color[500]` for primary or error colors and `color['A400']` for secondary colors
   */
  main: PropTypes.string.isRequired,
});

/**
 * A material-ui compliant color
 */
const color = PropTypes.oneOfType([colorComplex, colorSimple]);

/**
 * A material-ui compliant font
 */
const font = PropTypes.shape({
  color: PropTypes.string,
  fontFamily: PropTypes.string.isRequired,
  fontSize: PropTypes.string.isRequired,
  fontWeight: PropTypes.number.isRequired,
  letterSpacing: PropTypes.string,
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  marginLeft: PropTypes.string,
});

/**
 * A material-ui compliant palette type
 */
const paletteType = PropTypes.oneOf([THEME_PALETTE_TYPE_DARK, THEME_PALETTE_TYPE_LIGHT]);

/**
 * A material-ui theme
 */
const theme = PropTypes.shape({
  breakpoints: PropTypes.shape({
    between: PropTypes.func.isRequired,
    down: PropTypes.func.isRequired,
    keys: PropTypes.arrayOf(PropTypes.oneOf([`lg`, `md`, `sm`, `xl`, `xs`])).isRequired,
    only: PropTypes.func.isRequired,
    up: PropTypes.func.isRequired,
    values: PropTypes.shape({
      lg: PropTypes.number.isRequired,
      md: PropTypes.number.isRequired,
      sm: PropTypes.number.isRequired,
      xl: PropTypes.number.isRequired,
      xs: PropTypes.number.isRequired,
    }).isRequired,
    width: PropTypes.func.isRequired,
  }).isRequired,
  direction: PropTypes.oneOf([I18N_DIRECTION_LEFT_TO_RIGHT, I18N_DIRECTION_RIGHT_TO_LEFT]).isRequired,
  mixins: PropTypes.shape({
    gutters: PropTypes.func.isRequired,
    toolbar: PropTypes.shape({
      minHeight: PropTypes.number.isRequired,
    }).isRequired,
  }).isRequired,
  palette: PropTypes.shape({
    action: PropTypes.shape({
      active: PropTypes.string.isRequired,
      disabled: PropTypes.string.isRequired,
      disabledBackground: PropTypes.string.isRequired,
      hover: PropTypes.string.isRequired,
      hoverOpacity: PropTypes.number.isRequired,
      selected: PropTypes.string.isRequired,
    }),
    augmentColor: PropTypes.func.isRequired,
    background: PropTypes.shape({
      default: PropTypes.string.isRequired,
      paper: PropTypes.string.isRequired,
    }),
    common: PropTypes.shape({
      black: PropTypes.string.isRequired,
      white: PropTypes.string.isRequired,
    }).isRequired,
    contrastThreshold: PropTypes.number.isRequired,
    divider: PropTypes.string.isRequired,
    error: color.isRequired,
    getContrastText: PropTypes.func.isRequired,
    grey: color.isRequired,
    primary: color.isRequired,
    secondary: color.isRequired,
    text: PropTypes.shape({
      disabled: PropTypes.string.isRequired,
      hint: PropTypes.string.isRequired,
      primary: PropTypes.string.isRequired,
      secondary: PropTypes.string.isRequired,
    }).isRequired,
    tonalOffset: PropTypes.number.isRequired,
    type: PropTypes.oneOf([THEME_PALETTE_TYPE_DARK, THEME_PALETTE_TYPE_LIGHT]).isRequired,
  }).isRequired,
  shadows: PropTypes.arrayOf(PropTypes.string).isRequired,
  shape: PropTypes.shape({
    borderRadius: PropTypes.number.isRequired,
  }).isRequired,
  spacing: PropTypes.func.isRequired,
  transitions: PropTypes.shape({
    create: PropTypes.func.isRequired,
    duration: PropTypes.shape({
      complex: PropTypes.number.isRequired,
      enteringScreen: PropTypes.number.isRequired,
      leavingScreen: PropTypes.number.isRequired,
      short: PropTypes.number.isRequired,
      shorter: PropTypes.number.isRequired,
      shortest: PropTypes.number.isRequired,
      standard: PropTypes.number.isRequired,
    }).isRequired,
    easing: PropTypes.shape({
      easeIn: PropTypes.string.isRequired,
      easeInOut: PropTypes.string.isRequired,
      easeOut: PropTypes.string.isRequired,
      sharp: PropTypes.string.isRequired,
    }).isRequired,
    getAutoHeightDuration: PropTypes.func.isRequired,
  }).isRequired,
  typography: PropTypes.shape({
    body1: font.isRequired,
    body2: font.isRequired,
    button: font.isRequired,
    caption: font.isRequired,
    fontFamily: PropTypes.string.isRequired,
    fontSize: PropTypes.number.isRequired,
    fontWeightLight: PropTypes.number.isRequired,
    fontWeightMedium: PropTypes.number.isRequired,
    fontWeightRegular: PropTypes.number.isRequired,
    h1: font.isRequired,
    h2: font.isRequired,
    h3: font.isRequired,
    h4: font.isRequired,
    h5: font.isRequired,
    h6: font.isRequired,
    pxToRem: PropTypes.func.isRequired,
    round: PropTypes.func.isRequired,
    subtitle1: font.isRequired,
  }).isRequired,
  zIndex: PropTypes.shape({
    appBar: PropTypes.number.isRequired,
    drawer: PropTypes.number.isRequired,
    mobileStepper: PropTypes.number.isRequired,
    modal: PropTypes.number.isRequired,
    snackbar: PropTypes.number.isRequired,
    tooltip: PropTypes.number.isRequired,
  }).isRequired,
});

const ThemeStatePropTypes = {
  /**
   * A material-ui classes Object
   */
  classes,

  /**
   * A material-ui compliant color
   */
  color,

  /**
   * A material-ui compliant complex color
   */
  colorComplex,

  /**
   * A material-ui compliant simple color
   */
  colorSimple,

  /**
   * A material-ui compliant font
   */
  font,

  /**
   * A material-ui compliant palette type
   */
  paletteType,

  /**
   * A material-ui theme
   */
  theme,
};

export default ThemeStatePropTypes;
